'use client';

import { c as _c } from "react/compiler-runtime";
import type { NextPage } from 'next';
import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { QueryClientProvider, ReactQueryDevtools } from 'shared/components/ReactQueryTools';
import ApiClientErrorHandler from 'shared/components/ApiClienErrorHandler';
import { ErrorFallback, type FallbackProps } from 'shared/components/ErrorFallback';
import { GoogleTagManagerScript } from 'shared/components/GoogleTagManager';
import { IntercomScript } from 'shared/components/IntercomScript';
import { usePathname } from 'shared/hooks/useRouterPath';
import { Toaster } from 'shared/components/Toaster';
import { AuthProvider } from 'shared/context/AuthContext';
import { AlvieKuramaProvider } from 'shared/context/AlvieKuramaProvider';
import ErrorReporterProvider from 'shared/context/ErrorReporterContext';
import useBuildId from 'shared/hooks/useBuildId';
import { createQueryClient } from 'shared/reactQuery';
import { reloadCurrentPage } from 'shared/utils';
import { reportWebVitalsToGTM } from 'shared/webVitals';
import initLogRocket from 'shared/logRocket';
import { useReportWebVitals } from 'next/web-vitals';
import { initTranslations } from 'shared/translations/init';
initLogRocket();
initTranslations();
export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};
const BuildIdCheck = t0 => {
  const $ = _c(7);
  const {
    children
  } = t0;
  const {
    shouldReload
  } = useBuildId();
  const {
    pathnameWithQueryParams
  } = usePathname() ?? "";
  let t1;
  if ($[0] !== shouldReload) {
    t1 = () => {
      const handleRouteChange = () => {
        (async () => {
          const shouldReloadPage = await shouldReload();
          if (shouldReloadPage !== undefined && shouldReloadPage) {
            reloadCurrentPage();
          }
        })();
      };
      handleRouteChange();
    };
    $[0] = shouldReload;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== pathnameWithQueryParams || $[3] !== shouldReload) {
    t2 = [shouldReload, pathnameWithQueryParams];
    $[2] = pathnameWithQueryParams;
    $[3] = shouldReload;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== children) {
    t3 = <>{children}</>;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const ErrorFallbackUI = t0 => {
  const $ = _c(3);
  const {
    error,
    resetError
  } = t0;
  let t1;
  if ($[0] !== error || $[1] !== resetError) {
    t1 = <ErrorFallback error={error} resetError={resetError} hasDashboardFrame={false} />;
    $[0] = error;
    $[1] = resetError;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const BaseProviders = t0 => {
  const $ = _c(16);
  const {
    children
  } = t0;
  useReportWebVitals(reportWebVitalsToGTM);
  const [queryClient] = useState(createQueryClient);
  let t1;
  if ($[0] !== children) {
    t1 = <BuildIdCheck>{children}</BuildIdCheck>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ApiClientErrorHandler />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <AuthProvider>{t1}{t2}</AuthProvider>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ReactQueryDevtools />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== queryClient || $[7] !== t3) {
    t5 = <ErrorReporterProvider FallbackUI={ErrorFallbackUI}><QueryClientProvider client={queryClient}>{t3}{t4}</QueryClientProvider></ErrorReporterProvider>;
    $[6] = queryClient;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Toaster />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <AlvieKuramaProvider>{t5}{t6}</AlvieKuramaProvider>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <GoogleTagManagerScript />;
    t9 = <IntercomScript />;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7) {
    t10 = <>{t7}{t8}{t9}</>;
    $[14] = t7;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
};
export default BaseProviders;